import { ROW_HEIGHT } from './select.constants';
import { SelectOption } from './select-option-item.component';
import { SelectPropsValue } from './select.type';

export const getInitialExpandedItems = (options: SelectOption[]): Set<string> => {
    const expanded = new Set<string>();
    for (const option of options) {
        if (option.children && option.children.length > 0) {
            expanded.add(String(option.value));
        }
    }
    return expanded;
};

const flattenOptions = (options: SelectOption[]): SelectOption[] => {
    const flatOptions: SelectOption[] = [];
    for (const option of options) {
        flatOptions.push(option);
        if (option.children && option.children.length > 0) {
            flatOptions.push(...option.children);
        }
    }
    return flatOptions;
};

// owc-select-dropdown resolves the selection through the `owc-list-item[value="..."]` attribute
// selector, so callers may legitimately pair numeric option values with a stringified value.
const toOptionKeys = (values: unknown[]): Set<string> => new Set(values.map(String));

const getSelectedOptions = (options: SelectOption[], value: SelectPropsValue): SelectOption[] => {
    if (value == null) {
        return [];
    }
    const selectedKeys = toOptionKeys(Array.isArray(value) ? value : [value]);
    return flattenOptions(options).filter(({ value: optionValue }) => selectedKeys.has(String(optionValue)));
};

// The dropdown reads the selected label off the option currently in the DOM, so only the selected
// options that virtualization left out need a hidden stand-in.
export const getSelectedHiddenOptions = (
    options: SelectOption[],
    value: SelectPropsValue,
    renderedOptions: SelectOption[]
): SelectOption[] => {
    const renderedKeys = toOptionKeys(flattenOptions(renderedOptions).map(({ value: optionValue }) => optionValue));
    return getSelectedOptions(options, value).filter(
        ({ value: optionValue }) => !renderedKeys.has(String(optionValue))
    );
};

export const getSelectedOptionsLabel = (options: SelectOption[], value: SelectPropsValue): string =>
    getSelectedOptions(options, value)
        .map(({ text }) => text)
        .join('\n');

export const hasPlaceholderSelectedOption = (options: SelectOption[], value: SelectPropsValue): boolean =>
    getSelectedOptions(options, value).some(({ isPlaceholder }) => isPlaceholder === true);

export const computeRowHeight = (options: SelectOption[], expandedItems: Set<string>, index: number): number => {
    const option = options[index];
    if (!option) {
        return ROW_HEIGHT;
    }
    const childCount = expandedItems.has(String(option.value)) ? (option.children?.length ?? 0) : 0;
    return ROW_HEIGHT + childCount * ROW_HEIGHT;
};
